import * as React from 'react';
import { useSelector } from 'react-redux';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { CommandBarButton, PrimaryButton, TextField } from '@fluentui/react';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import moment from 'moment-timezone';
import { SelectOption } from 'formComponents/Select/types';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { useBetreuer, useBetreuerByNr } from 'api/betreuer';
import { authSelector } from 'store/auth/selector';
import { Label } from '@fluentui/react/lib/Label';
import { formStyles } from 'styles/formStyles';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { MultiSelect } from 'formComponents/Select/MultiSelect';
import { useBranchenAsOptions } from 'api/branche';
import { useErweiterteSuchenAsOptions } from 'api/erweiterteSuche';
import { useInteresseFuerProdukteAsOptions } from 'api/interesseFuerProdukte';
import { useProduktgruppenAsOptions } from 'api/produktgruppe';
import { FormDecimalField } from 'formComponents/FormDecimalField';
import { AuftragStatusMulti } from 'formComponents/lookups/AuftragStatusMulti';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { useHistory, useLocation } from 'react-router-dom';
import { getKunde } from 'api/kunde';
import { isEqual } from 'lodash';
import { Auftragsuebersicht } from './Auftragsuebersicht';
import { AuftragsuebersichtList } from './AuftraguebersichtList';
import { generateReportPrintUrl } from '../utils';
import { Auftragsuebersicht3d } from './Auftragsuebersicht3d';
import { FilterValues } from './types';

const styles = mergeStyleSets({
    container: {
        margin: 0,
        marginTop: 5,
        padding: 0,
        display: 'grid',
        gridTemplateRows: 'max-content max-content max-content 1fr',
        gridRowGap: 5,
        height: 'calc(100vh - 75px)',
    },
    searchLine1: {
        display: 'grid',
        gridTemplateColumns: '160px 160px 180px 180px 1fr 1fr 1fr max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    searchLine2: {
        display: 'grid',
        gridTemplateColumns: '100px 100px 100px 100px 180px 180px 1fr 1fr 1fr',
        gridColumnGap: 10,
    },
    searchLine3: {
        display: 'grid',
        gridTemplateColumns: '160px 160px 180px 180px repeat(3, max-content) 1fr',
        gridColumnGap: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
    reset: {
        textAlign: 'right',
        marginTop: 8,
    },
});

const alleBetreuerOption = { value: '__all__', label: 'Alle Betreuer' };

const defaultFilterValues: FilterValues = {
    statusList: [], // 1, 9, 4
    datumVon: moment().startOf('month').format('YYYY-MM-DD'), // '2021-10-01'
    datumBis: moment().format('YYYY-MM-DD'), // '2021-10-xx'
    kNr: undefined,
    kundePlzVon: '',
    kundePlzBis: '',
    kundeUmsatzTsdVon: 0,
    kundeUmsatzTsdBis: 0,
    kundeBranchen: [],
    kundeErweiterteSuchen: [],
    kundeInteresseFuerProdukte: [],
    kundeBetreuer: undefined,
    kundeTelBetreuer: undefined,
    betreuer: undefined,
    betreuerGebiet: false,
    telBetreuer: undefined,
    telBetreuerGebiet: false,
    produktgruppen: [],
    previousYear1: true,
    previousYear2: false,
    previousYear3: false,
    showBetreuer: true,
    showTelBetreuer: true,
    showDetails: false,
    show3d: false,
};

export const AuftragsuebersichtView: React.FC = () => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();
    const branchenOptions = useBranchenAsOptions(true);
    const interesseFuerProdukteOptions = useInteresseFuerProdukteAsOptions(true);
    const erweiterteSuchenOptions = useErweiterteSuchenAsOptions(true);
    const produktgruppenOptions = useProduktgruppenAsOptions();
    const [filterValues, setFilterValues] = React.useState<FilterValues>({
        ...defaultFilterValues,
    });
    const [appliedFilterValues, setAppliedFilterValues] = React.useState<FilterValues | null>(null);
    const isApplied = isEqual(filterValues, appliedFilterValues);

    const history = useHistory();
    const location = useLocation();

    const urlSearchParams = new URLSearchParams(location.search);
    const urlKNr = urlSearchParams.get('kNr');

    React.useEffect(() => {
        if (urlKNr) {
            history.replace({
                search: '',
            });

            const kNr = Number.parseInt(urlKNr, 10) || 0;
            if (kNr) {
                getKunde({ nr: kNr }).then((kunden) => {
                    if (kunden?.length === 1) {
                        setFilterValues({ ...filterValues, kNr: kunden[0].nr, kName: kunden[0].name1 });
                    }
                });
            }
        }
    }, [filterValues, history, location, setFilterValues, urlKNr]);

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle === 'AD') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            options.push(alleBetreuerOption);
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const kundeBranchenValues = React.useMemo(
        () => branchenOptions.filter((o) => filterValues.kundeBranchen?.includes(parseInt(o.value, 10))),
        [branchenOptions, filterValues.kundeBranchen],
    );

    const kundeInteresseFuerProdukteValues = React.useMemo(
        () =>
            interesseFuerProdukteOptions.filter((o) =>
                filterValues.kundeInteresseFuerProdukte?.includes(parseInt(o.value, 10)),
            ),
        [interesseFuerProdukteOptions, filterValues.kundeInteresseFuerProdukte],
    );

    const kundeErweiterteSuchenValues = React.useMemo(
        () =>
            erweiterteSuchenOptions.filter((o) => filterValues.kundeErweiterteSuchen?.includes(parseInt(o.value, 10))),
        [erweiterteSuchenOptions, filterValues.kundeErweiterteSuchen],
    );

    const produktgruppenValues = React.useMemo(
        () => produktgruppenOptions.filter((o) => filterValues.produktgruppen?.includes(parseInt(o.value, 10))),
        [produktgruppenOptions, filterValues.produktgruppen],
    );

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={styles.container}>
            <div className={styles.searchLine1}>
                <FormDatePicker
                    value={filterValues.datumVon}
                    label="Datum Von (Auftrag)"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumVon: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <FormDatePicker
                    value={filterValues.datumBis}
                    label="Datum Bis (Auftrag)"
                    options={{
                        required: true,
                    }}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            datumBis: moment(newValue).format('YYYY-MM-DD') + 'T00:00:00',
                        });
                    }}
                />

                <div>
                    <Label>Betreuer (Auftrag)</Label>
                    <Select
                        name="betreuer"
                        value={
                            filterValues.betreuer
                                ? {
                                      value: filterValues.betreuer.toString(),
                                      label: betreuerByNr[filterValues.betreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                betreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Tel. Betreuer (Auftrag)</Label>
                    <Select
                        name="telBetreuer"
                        value={
                            filterValues.telBetreuer
                                ? {
                                      value: filterValues.telBetreuer.toString(),
                                      label: betreuerByNr[filterValues.telBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                telBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Produktgruppen</Label>
                    <MultiSelect
                        name="produktgruppen"
                        value={produktgruppenValues}
                        placeholder="Produktgruppen..."
                        options={produktgruppenOptions}
                        onChange={(values: ValueType<SelectOption, true>): void => {
                            setFilterValues({
                                ...filterValues,
                                produktgruppen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                            });
                        }}
                    />
                </div>

                <div>
                    <Label>Status</Label>
                    <AuftragStatusMulti
                        value={filterValues.statusList.map((v) => v.toString())}
                        renderLabel="placeholder"
                        onChange={(newValues) => {
                            setFilterValues({
                                ...filterValues,
                                statusList: newValues.map((v) => parseInt(v, 10)),
                            });
                        }}
                    />
                </div>

                <KundeSelect
                    label="Kunde"
                    kNr={filterValues.kNr}
                    name={filterValues.kName}
                    allowEmpty
                    onChange={(newKNr, newKunde): void => {
                        setFilterValues({
                            ...filterValues,
                            kNr: newKNr || undefined,
                            kName: newKunde?.name1 || '',
                        });
                    }}
                />

                <div className={styles.actionButtons}>
                    {auth.rolle !== 'AD' && (
                        <PrimaryButton
                            disabled={!isApplied}
                            onClick={() => {
                                window.open(
                                    generateReportPrintUrl('auftragsuebersicht', {
                                        statusList: filterValues.statusList.join(','),
                                        datumVon: filterValues.datumVon,
                                        datumBis: filterValues.datumBis,
                                        kNr: filterValues.kNr,
                                        kundePlzVon: filterValues.kundePlzVon,
                                        kundePlzBis: filterValues.kundePlzBis,
                                        kundeUmsatzTsdVon: filterValues.kundeUmsatzTsdVon,
                                        kundeUmsatzTsdBis: filterValues.kundeUmsatzTsdBis,
                                        kundeBranchen: (filterValues.kundeBranchen || []).join(','),
                                        kundeInteresseFuerProdukte: (
                                            filterValues.kundeInteresseFuerProdukte || []
                                        ).join(','),
                                        kundeErweiterteSuchen: (filterValues.kundeErweiterteSuchen || []).join(','),
                                        kundeBetreuer: filterValues.kundeBetreuer,
                                        kundeTelBetreuer: filterValues.kundeTelBetreuer,
                                        betreuer: filterValues.betreuer,
                                        betreuerGebiet: filterValues.betreuerGebiet,
                                        telBetreuer: filterValues.telBetreuer,
                                        telBetreuerGebiet: filterValues.telBetreuerGebiet,
                                        showBetreuer: filterValues.showBetreuer,
                                        showTelBetreuer: filterValues.showTelBetreuer,
                                        produktgruppen: (filterValues.produktgruppen || []).join(','),
                                        showDetails: filterValues.showDetails,
                                        previousYear1: filterValues.previousYear1,
                                        previousYear2: filterValues.previousYear2,
                                        previousYear3: filterValues.previousYear3,
                                        show3d: filterValues.show3d,
                                    }),
                                );
                            }}
                            text="Drucken"
                        />
                    )}
                    <PrimaryButton
                        text="Start"
                        disabled={isApplied}
                        onClick={() => setAppliedFilterValues(filterValues)}
                    />
                </div>
            </div>

            <div className={styles.searchLine2}>
                <TextField
                    placeholder="K-PLZ (von)"
                    value={filterValues.kundePlzVon}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundePlzVon: newValue || '',
                        });
                    }}
                />
                <TextField
                    placeholder="K-PLZ (bis)"
                    value={filterValues.kundePlzBis}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundePlzBis: newValue || '',
                        });
                    }}
                />

                <FormDecimalField
                    placeholder="K-Umsatz (von TSD)"
                    value={filterValues.kundeUmsatzTsdVon}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdVon: newValue,
                        });
                    }}
                />

                <FormDecimalField
                    placeholder="K-Umsatz (bis TSD)"
                    value={filterValues.kundeUmsatzTsdBis}
                    onChange={(newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdBis: newValue,
                        });
                    }}
                />

                {/*
                <TextField
                    placeholder="K-Umsatz (von TSD)"
                    type="number"
                    value={
                        filterValues.kundeUmsatzTsdVon === null || filterValues.kundeUmsatzTsdVon === undefined
                            ? ''
                            : filterValues.kundeUmsatzTsdVon.toString()
                    }
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdVon: (newValue || '').trim() === '' ? null : parseInt(newValue || '', 10),
                        });
                    }}
                />

                <TextField
                    placeholder="K-Umsatz (bis TSD)"
                    type="number"
                    value={
                        filterValues.kundeUmsatzTsdBis === null || filterValues.kundeUmsatzTsdBis === undefined
                            ? ''
                            : filterValues.kundeUmsatzTsdBis.toString()
                    }
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            kundeUmsatzTsdBis: (newValue || '').trim() === '' ? null : parseInt(newValue || '', 10),
                        });
                    }}
                />
                */}

                <div>
                    <Select
                        name="kundeBetreuer"
                        value={
                            filterValues.kundeBetreuer
                                ? {
                                      value: filterValues.kundeBetreuer.toString(),
                                      label: betreuerByNr[filterValues.kundeBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                kundeBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <div>
                    <Select
                        name="kundeTelBetreuer"
                        value={
                            filterValues.kundeTelBetreuer
                                ? {
                                      value: filterValues.kundeTelBetreuer.toString(),
                                      label: betreuerByNr[filterValues.kundeTelBetreuer]?.name,
                                  }
                                : alleBetreuerOption
                        }
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            setFilterValues({
                                ...filterValues,
                                kundeTelBetreuer:
                                    newValue?.value && newValue?.value !== '__all__'
                                        ? parseInt(newValue?.value, 10)
                                        : undefined,
                            });
                        }}
                    />
                </div>

                <MultiSelect
                    name="filterBranche"
                    value={kundeBranchenValues}
                    placeholder="Branchen..."
                    options={branchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeBranchen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                        });
                    }}
                />

                <MultiSelect
                    name="filterInteresseFuerProdukte"
                    value={kundeInteresseFuerProdukteValues}
                    placeholder="Int. f. Prod..."
                    options={interesseFuerProdukteOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeInteresseFuerProdukte: ((values || []) as SelectOption[]).map((o) =>
                                parseInt(o.value, 10),
                            ),
                        });
                    }}
                />

                <MultiSelect
                    name="filterErweiterteSuchen"
                    value={kundeErweiterteSuchenValues}
                    placeholder="Erw. Suche..."
                    options={erweiterteSuchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            kundeErweiterteSuchen: ((values || []) as SelectOption[]).map((o) => parseInt(o.value, 10)),
                        });
                    }}
                />
            </div>

            <div className={styles.searchLine3}>
                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Alle Betreuer"
                    disabled={filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.showBetreuer}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showBetreuer: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Alle Tel.-Betreuer"
                    disabled={filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.showTelBetreuer}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showTelBetreuer: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Betreuer - nur Gebiet"
                    disabled={!filterValues.betreuer || filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.betreuerGebiet}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            betreuerGebiet: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Tel.-Betr. - nur Gebiet"
                    disabled={!filterValues.telBetreuer || filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.telBetreuerGebiet}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            telBetreuerGebiet: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Details"
                    checked={filterValues.showDetails}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            showDetails: !!checked,
                            show3d: checked ? false : filterValues.show3d,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="3D"
                    checked={filterValues.show3d}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            show3d: !!checked,
                            showDetails: checked ? false : filterValues.showDetails,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Vorjahr 1"
                    disabled={filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.previousYear1}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            previousYear1: !!checked,
                        });
                    }}
                />

                {/*
                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Vorjahr 2"
                    disabled={filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.previousYear2}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            previousYear2: !!checked,
                        });
                    }}
                />

                <Checkbox
                    className={formStyles.checkboxInsideForm}
                    label="Vorjahr 3"
                    disabled={filterValues.showDetails || filterValues.show3d}
                    checked={filterValues.previousYear3}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            previousYear3: !!checked,
                        });
                    }}
                />
                */}

                <div className={styles.reset}>
                    <CommandBarButton text="Zurücksetzen" onClick={() => setFilterValues(defaultFilterValues)} />
                </div>
            </div>

            {appliedFilterValues && appliedFilterValues.showDetails && (
                <AuftragsuebersichtList
                    statusList={appliedFilterValues.statusList}
                    datumVon={appliedFilterValues.datumVon}
                    datumBis={appliedFilterValues.datumBis}
                    kNr={appliedFilterValues.kNr}
                    kundePlzVon={appliedFilterValues.kundePlzVon}
                    kundePlzBis={appliedFilterValues.kundePlzBis}
                    kundeUmsatzTsdVon={appliedFilterValues.kundeUmsatzTsdVon}
                    kundeUmsatzTsdBis={appliedFilterValues.kundeUmsatzTsdBis}
                    kundeBranchen={appliedFilterValues.kundeBranchen}
                    kundeInteresseFuerProdukte={appliedFilterValues.kundeInteresseFuerProdukte}
                    kundeErweiterteSuchen={appliedFilterValues.kundeErweiterteSuchen}
                    kundeBetreuer={appliedFilterValues.kundeBetreuer}
                    kundeTelBetreuer={appliedFilterValues.kundeTelBetreuer}
                    betreuer={appliedFilterValues.betreuer}
                    betreuerGebiet={appliedFilterValues.betreuerGebiet}
                    telBetreuer={appliedFilterValues.telBetreuer}
                    telBetreuerGebiet={appliedFilterValues.telBetreuerGebiet}
                    produktgruppen={appliedFilterValues.produktgruppen}
                    showDetails={appliedFilterValues.showDetails}
                    previousYear1={appliedFilterValues.previousYear1}
                    previousYear2={appliedFilterValues.previousYear2}
                    previousYear3={appliedFilterValues.previousYear3}
                />
            )}

            {appliedFilterValues && appliedFilterValues.show3d && (
                <Auftragsuebersicht3d
                    statusList={appliedFilterValues.statusList}
                    datumVon={appliedFilterValues.datumVon}
                    datumBis={appliedFilterValues.datumBis}
                    kNr={appliedFilterValues.kNr}
                    kundePlzVon={appliedFilterValues.kundePlzVon}
                    kundePlzBis={appliedFilterValues.kundePlzBis}
                    kundeUmsatzTsdVon={appliedFilterValues.kundeUmsatzTsdVon}
                    kundeUmsatzTsdBis={appliedFilterValues.kundeUmsatzTsdBis}
                    kundeBranchen={appliedFilterValues.kundeBranchen}
                    kundeInteresseFuerProdukte={appliedFilterValues.kundeInteresseFuerProdukte}
                    kundeErweiterteSuchen={appliedFilterValues.kundeErweiterteSuchen}
                    kundeBetreuer={appliedFilterValues.kundeBetreuer}
                    kundeTelBetreuer={appliedFilterValues.kundeTelBetreuer}
                    produktgruppen={appliedFilterValues.produktgruppen}
                    betreuer={appliedFilterValues.betreuer}
                    betreuerGebiet={appliedFilterValues.betreuerGebiet}
                    telBetreuer={appliedFilterValues.telBetreuer}
                    telBetreuerGebiet={appliedFilterValues.telBetreuerGebiet}
                    showDetails={appliedFilterValues.showDetails}
                    previousYear1={appliedFilterValues.previousYear1}
                    previousYear2={appliedFilterValues.previousYear2}
                    previousYear3={appliedFilterValues.previousYear3}
                />
            )}

            {appliedFilterValues && !appliedFilterValues.showDetails && !appliedFilterValues.show3d && (
                <Auftragsuebersicht
                    statusList={appliedFilterValues.statusList}
                    datumVon={appliedFilterValues.datumVon}
                    datumBis={appliedFilterValues.datumBis}
                    kNr={appliedFilterValues.kNr}
                    kundePlzVon={appliedFilterValues.kundePlzVon}
                    kundePlzBis={appliedFilterValues.kundePlzBis}
                    kundeUmsatzTsdVon={appliedFilterValues.kundeUmsatzTsdVon}
                    kundeUmsatzTsdBis={appliedFilterValues.kundeUmsatzTsdBis}
                    kundeBranchen={appliedFilterValues.kundeBranchen}
                    kundeInteresseFuerProdukte={appliedFilterValues.kundeInteresseFuerProdukte}
                    kundeErweiterteSuchen={appliedFilterValues.kundeErweiterteSuchen}
                    kundeBetreuer={appliedFilterValues.kundeBetreuer}
                    kundeTelBetreuer={appliedFilterValues.kundeTelBetreuer}
                    produktgruppen={appliedFilterValues.produktgruppen}
                    betreuer={appliedFilterValues.betreuer}
                    betreuerGebiet={appliedFilterValues.betreuerGebiet}
                    telBetreuer={appliedFilterValues.telBetreuer}
                    telBetreuerGebiet={appliedFilterValues.telBetreuerGebiet}
                    showBetreuer={appliedFilterValues.showBetreuer}
                    showTelBetreuer={appliedFilterValues.showTelBetreuer}
                    showDetails={appliedFilterValues.showDetails}
                    previousYear1={appliedFilterValues.previousYear1}
                    previousYear2={appliedFilterValues.previousYear2}
                    previousYear3={appliedFilterValues.previousYear3}
                />
            )}
        </div>
    );
};
